"use client";

import { useContext, useState } from "react";
import { FiTrendingUp } from "react-icons/fi";
import Layout from "../../components/layout/Layout";
import TableSection from "./components/TableSection";
import dayjs from "dayjs";
import { Box, Modal } from "@mui/material";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro";
import { style } from "../Dashboard/Dashboard";
import { ServiceContext } from "../../hooks/context/ServiceContext";
import Tab from "../Competitions/components/Tab";
import Overview from "./components/Overview";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../hooks/api/analytics";
import PlayersTableSection from "./components/PlayersTableSection";
import QuizzesTableSection from "./components/QuizzesTableSection";



function Analytics() {
  const { service: network } = useContext(ServiceContext);
  // console.log("Network in Analytics Page:", network);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [analyticsChartFilter, setAnalyticsChartFilter] = useState("all");
  const [activeTimeLine, setActiveTimeLine] = useState("week");
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { data: analyticsData, isLoading, error } = useQuery({
    queryKey: [
      "get-analytics",
      startDate,
      endDate,
      activeTimeLine,
      network,
      analyticsChartFilter,
    ],
    queryFn: async () => {
      const response = await api.getAnalyticsCardData({
        startDate,
        endDate,
        activeTimeLine,
        network,
        analyticsChartFilter,
      });
      return response;
    },
  });

// console.log("Analytics Data:", analyticsData);

  const handleTimeline = () => {
    if (activeTimeLine !== "custom") {
      setStartDate(
        dayjs()
          .startOf(activeTimeLine as "week")
          .format("YYYY-MM-DD")
      );
      setEndDate(
        dayjs()
          .endOf(activeTimeLine as "week")
          .format("YYYY-MM-DD")
      );
    }
  };

  const [selectedValues, setSelectedValues] = useState<string[]>([
    "userGrowth",
    "engagementMetrics",
    "retentionAnalysis",
    "churnAnalysis",
    "activityBreakdown",
    "analyticsReports",
  ]);

  const handleCheckboxChange = (value: string) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(selectedValues.filter((v) => v !== value));
    } else {
      setSelectedValues([...selectedValues, value]);
    }
  };

  const tabsData = [
    {
      label: `Overview`,
      content: <Overview data={analyticsData} isLoading={isLoading} error={error} setAnalyticsChartFilter={setAnalyticsChartFilter} selectedValues={selectedValues} />,
    },
    {
      label: network === 'mtn' ? `Top Quizzes` : `Top Games`,
      content: network === 'mtn' ? (
        <QuizzesTableSection
          selectedValues={selectedValues}
          chartData={analyticsData?.data?.topQuizzes}
          startDate={startDate}
          endDate={endDate}
          activeTimeLine={activeTimeLine}
          network={network}
          analyticsChartFilter={analyticsChartFilter}
          pageNumber={pageNumber}
          pageSize={pageSize}
          searchTerm={searchTerm}
          setPageSize={setPageSize}
          setPageNumber={setPageNumber}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          error={error}
        />
      ) : (
        <TableSection
          selectedValues={selectedValues}
          chartData={analyticsData?.data?.topGames}
          startDate={startDate}
          endDate={endDate}
          activeTimeLine={activeTimeLine}
          network={network}
          analyticsChartFilter={analyticsChartFilter}
          pageNumber={pageNumber}
          pageSize={pageSize}
          searchTerm={searchTerm}
          setPageSize={setPageSize}
          setPageNumber={setPageNumber}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          error={error}
        />
      ),
    },
    {
      label: `Top Players`,
      content: (
        <PlayersTableSection
          selectedValues={selectedValues}
          chartData={analyticsData?.data?.topPlayers}
          startDate={startDate}
          endDate={endDate}
          activeTimeLine={activeTimeLine}
          network={network}
          analyticsChartFilter={analyticsChartFilter}
          pageNumber={pageNumber}
          pageSize={pageSize}
          searchTerm={searchTerm}
          setPageSize={setPageSize}
          setPageNumber={setPageNumber}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          error={error}
        />
      ),
    },
  ];

  return (
    <Layout
      pageName="Analytics"
      icon={<FiTrendingUp />}
      handleTimeLine={handleTimeline}
      handleOpenCalendar={handleOpen}
      activeTimeLine={activeTimeLine}
      setActiveTimeLine={setActiveTimeLine}
      metricsTitle="Analytics"
      showMetrics={false}
      showFilter2={false}
      handleMetricsCheckboxChange={handleCheckboxChange}
      metricsSelectedValues={selectedValues}
      metricsOptions={[
        { label: "User Growth", value: "userGrowth" },
        { label: "Engagement Metrics", value: "engagementMetrics" },
        { label: "Retention Analysis", value: "retentionAnalysis" },
        { label: "Churn Analysis", value: "churnAnalysis" },
        { label: "Activity Breakdown", value: "activityBreakdown" },
        { label: "Analytics Reports", value: "analyticsReports" },
      ]}
    >
     <div className="mt-6">
         <Tab tabsData={tabsData} />
     </div>
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <StaticDateRangePicker
            calendars={2}
            slotProps={{
              actionBar: { actions: [] },
            }}
            onChange={(date: any) => {
              const formattedStartDate = dayjs(date[0]).format("YYYY-MM-DD");
              const formattedEndDate = dayjs(date[1]).format("YYYY-MM-DD");
              if (
                dayjs(formattedStartDate).isValid() &&
                dayjs(formattedEndDate).isValid()
              ) {
                setStartDate(formattedStartDate);
                setEndDate(formattedEndDate);
                handleClose();
              }
            }}
          />
        </Box>
      </Modal>
    </Layout>
  );
}

export default Analytics;
